import type { Validation, ValidationArgs } from '@vuelidate/core';
import type { PartialRecordError } from 'types/Form';

export function $truncate(str: string, n = 75, ellipsis = '...'): string {
  return str.length > n ? `${str.substr(0, n - 1)}${ellipsis}` : str;
}

export function $capitalize(str: string): string {
  return str ? str.toString().charAt(0).toUpperCase() + str.slice(1) : '';
}

export function $capitalizeSentence(str: string): string {
  const sentence = str.split(' ');

  for (let i = 0; i < sentence.length; i++) {
    sentence[i] = $capitalize(sentence[i]);
  }

  return sentence.join(' ');
}

export function $sentence(arr: any, conjunction = 'and'): string {
  if (Array.isArray(arr)) {
    const tmpSentence = [...arr];
    if (tmpSentence.length <= 2) {
      return tmpSentence.join(` ${conjunction} `);
    }

    const last = tmpSentence.pop();
    return tmpSentence.join(', ') + ` ${conjunction} ` + last;
  }

  return arr;
}

export function $parseToSnakeCase(str: string): string {
  return str ? str.replace(/\s+/g, '_').toLowerCase() : '';
}

export function $parseToFormData(data: any, _formData?: FormData, parentKey?: any): FormData {
  let formData = _formData || new FormData();
  if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
    /**
     * NOTE: Special case scenario for `File upload` objects
     * where we want to pass the file
     *
     * TODO: Better checker for it? Not just `file` var
     */
    if (data.file) {
      formData.append(parentKey, data.file);
    } else {
      Object.keys(data).forEach((key) => {
        formData = $parseToFormData(data[key], formData, parentKey ? `${parentKey}[${key}]` : key);
      });
    }
  } else {
    const value = data == null ? '' : data;
    formData.append(parentKey, value);
  }

  return formData;
}

export function $isObject(value: any): boolean {
  return typeof value === 'object' && !Array.isArray(value) && value !== null;
}

export function $toSlug(str: string): string {
  return str
    .toLowerCase() // LowerCase
    .replace(/\s+/g, '-') // space to -
    .replace(/&/g, '-and-') // & to and
    .replace(/--/g, '-'); // -- to -
}

export const $apiErrorTransformer = <T>(errors: Partial<Record<keyof T, string[]>>): Record<keyof T, string> => {
  return Object.keys(errors).reduce(
    (prev, curr) => {
      if (errors[curr as keyof T] !== undefined) {
        prev[curr as keyof T] = errors[curr as keyof T]?.[0] || '';
      }

      return prev;
    },
    {} as Record<keyof T, string>
  );
};

export function $parsedError<T>(
  form: T,
  validator: globalThis.Ref<Validation<ValidationArgs<Partial<T>>, T>>,
  otherErrors?: PartialRecordError<T>
) {
  return Object.keys(form as object).reduce(
    (prev, curr) => {
      const otherError = (otherErrors && otherErrors[curr as keyof T]) || '';

      prev[curr as keyof T] = otherError || validator.value[curr]?.$errors[0]?.$message;

      return prev;
    },
    {} as Record<keyof T, string | undefined>
  );
}

export function $formatCurrency(value: number | string, currency = 'USD', customUserLang = null): string {
  const userLang = navigator.language;
  const _value = typeof value === 'string' ? parseFloat(value) : value;

  return new Intl.NumberFormat(customUserLang ?? userLang, {
    style: 'currency',
    currency,
  }).format(_value);
}

interface ApiErrorResponse {
  response?: {
    _data?: {
      message?: string;
    };
  };
}

export function $isApiError<TApiErrorResponseModel extends ApiErrorResponse>(
  error: unknown
): error is TApiErrorResponseModel {
  return !!(
    error &&
    typeof error === 'object' &&
    'response' in error &&
    (error as TApiErrorResponseModel).response?._data !== undefined
  );
}

export function $parseErrorMessage(error: unknown, defaultMessage: string = 'An error occurred'): string {
  if ($isApiError(error)) {
    return error.response?._data?.message || defaultMessage;
  }

  if (error instanceof Error) {
    return error.message || defaultMessage;
  }

  if (typeof error === 'string') {
    return error;
  }

  return defaultMessage;
}

export function getErrorMessage(
  errorSrc: any,
  defaultMessage: string = 'An error occurred, Please try again later.'
): string {
  const error = unref(errorSrc);
  if (typeof error === 'string') {
    return error;
  }

  return $parseErrorMessage(errorSrc, defaultMessage);
}
